/**
 * Idioma de la interfaz.
 *
 * El texto de la aplicación se escribe en español y esa frase es la clave:
 * `t('Ajustes')` devuelve «Settings» en inglés y, si una frase todavía no
 * está traducida, se queda en español. Así se puede traducir página a página
 * sin romper nada. Los diccionarios viven en `src/i18n/`.
 *
 * Como el tema, la elección se guarda en este dispositivo; si no hay ninguna,
 * se toma la del navegador. Al cambiar, `main.tsx` vuelve a montar la
 * aplicación para que todo se pinte en el idioma nuevo.
 */

import { useSyncExternalStore } from 'react';

import en from '@/i18n/en';
import pt from '@/i18n/pt';

export type Language = 'es' | 'en' | 'pt';

/** Idiomas disponibles, cada uno con su nombre en su propio idioma. */
export const LANGUAGES: { id: Language; name: string }[] = [
  { id: 'es', name: 'Español' },
  { id: 'en', name: 'English' },
  { id: 'pt', name: 'Português' },
];

const DICTIONARIES: Record<Language, Record<string, string> | null> = { es: null, en, pt };

/** Etiqueta para `toLocaleString` y compañía. */
const LOCALES: Record<Language, string> = { es: 'es', en: 'en', pt: 'pt' };

const KEY = 'uachess.lang';
const listeners = new Set<() => void>();

function isLanguage(value: unknown): value is Language {
  return value === 'es' || value === 'en' || value === 'pt';
}

function read(): Language {
  try {
    const value = localStorage.getItem(KEY);
    if (isLanguage(value)) return value;
  } catch {
    /* sin almacenamiento: se usa el del navegador */
  }
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag?.slice(0, 2).toLowerCase();
    if (isLanguage(base)) return base;
  }
  return 'es';
}

let current = read();
document.documentElement.lang = current;

export function getLanguage(): Language {
  return current;
}

export function setLanguage(value: Language): void {
  if (value === current) return;
  current = value;
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* se aplica igualmente en esta visita */
  }
  document.documentElement.lang = value;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLanguage(): Language {
  return useSyncExternalStore(subscribe, () => current);
}

/** Etiqueta de idioma para fechas y números: `n.toLocaleString(locale())`. */
export function locale(): string {
  return LOCALES[current];
}

/**
 * Traduce una frase escrita en español. Las llaves se rellenan con `vars`:
 * `t('Hola, {name}', { name })`.
 */
export function t(text: string, vars?: Record<string, string | number>): string {
  const translated = DICTIONARIES[current]?.[text] ?? text;
  if (!vars) return translated;
  return translated.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Elige entre singular y plural: `plural(n, '{n} partida', '{n} partidas')`. */
export function plural(count: number, one: string, other: string): string {
  return t(count === 1 ? one : other, { n: count.toLocaleString(locale()) });
}
